import * as React from "react";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AuthenticatedTemplate, MsalProvider, UnauthenticatedTemplate } from "@azure/msal-react";
import { AuthenticationResult, EventMessage, EventType, IPublicClientApplication } from "@azure/msal-browser";
import { CustomNavigationClient } from "./utils/NavigationClient";

import Home from "./routes/Home";
import DarkModeSwitch, { useDarkMode } from "./components/DarkModeSwitch";

import Questions from "./routes/Questions";
import { Box, Grommet, Stack } from "grommet";
import Theme from "./utils/Theme";
import { SkeletonTheme } from "react-loading-skeleton";
import { normalizeColor } from "grommet/utils";
import Splash from "./routes/Splash";

import { msalInstance } from ".";
import SkeletonWrapper from "./components/SkeltonWrapper";
import ErrorBoundary from "./components/ErrorBoundary";
import AsteroidsGame from "./components/AsterioidsGame";
import { is } from "date-fns/locale";
import { Account, getUser, useUser } from "./utils/AccountStore";
import { loginRequest } from "./authConfig";

type AppProps = {
    pca: IPublicClientApplication;
};

function App({ pca }: AppProps) {
    const navigate = useNavigate();
    const navigationClient = new CustomNavigationClient(navigate);
    pca.setNavigationClient(navigationClient);

    const [isDarkMode, toggleDarkMode] = useDarkMode();

    const [grommetProps, setGrommetProps] = React.useState({});

    const [isLoading, setIsLoading] = React.useState(false);

    const [user, setUser] = React.useState<Account | null>(null);

    React.useEffect(() => {
        // const locale = new Intl.Locale(navigator.language);
        // //@ts-ignore
        // if (locale.textInfo.direction == "rtl") {
        //     setGrommetProps({ dir: "rtl" });
        // } else {
        //     setGrommetProps({});
        // }

        pca.addEventCallback((event: EventMessage) => {
            setIsLoading(false);
            if (event.eventType === EventType.HANDLE_REDIRECT_END || event.eventType === EventType.INITIALIZE_END) {
                getUser().then((user) => {
                    setUser(user);
                });
            }
        });
    }, []);

    React.useEffect(() => {
        if (isDarkMode) {
            toggleDarkMode();
        }
    }, [isDarkMode]);

    React.useEffect(() => {
        if (user?.isAuthenticated) {
            console.log(user.getContextID(), DefaultOrganization);
            if (user.getContextID() != DefaultOrganization) {
                msalInstance.loginRedirect({
                    ...loginRequest,
                    prompt: "login",
                    extraQueryParameters: {
                        context: DefaultOrganization,
                        ui_locales: `switchTo:true`,
                    },
                });
            }
        }
    }, [user]);

    return (
        <MsalProvider instance={pca}>
            <Grommet {...grommetProps} full theme={Theme} themeMode={"light"}>
                <SkeletonTheme baseColor={normalizeColor("background-back", Theme, false)} highlightColor={normalizeColor("background-front", Theme, false)}>
                    <SkeletonWrapper loading={isLoading}>
                        <Box background={{ color: "background" }}>
                            {/* <DarkModeSwitch></DarkModeSwitch> */}

                            <Stack guidingChild={1}>
                                <Box>{user?.getContextID() == DefaultOrganization ? "🟢" : "🔴"}</Box>
                                <Box align="center" style={{ backgroundSize: "cover" }} height={{ min: "100vh" }}>
                                    <Box width={{ min: "50vw", max: "800px" }} pad={{ left: "medium", right: "medium" }} style={{ position: "relative" }}>
                                        <ErrorBoundary>
                                            <AuthenticatedTemplate>
                                                <Routes>
                                                    <Route path="/knowledgeCheck/:id" element={<Questions></Questions>}></Route>
                                                    <Route path="/home" element={<Home></Home>}></Route>
                                                    <Route path="/*" element={<Home></Home>}></Route>
                                                    <Route path="/game" element={<AsteroidsGame></AsteroidsGame>}></Route>
                                                </Routes>
                                            </AuthenticatedTemplate>

                                            <UnauthenticatedTemplate>
                                                <Routes>
                                                    <Route path="/*" element={<Splash></Splash>}></Route>
                                                    <Route path="/game" element={<AsteroidsGame></AsteroidsGame>}></Route>
                                                </Routes>
                                            </UnauthenticatedTemplate>
                                        </ErrorBoundary>
                                    </Box>
                                </Box>
                            </Stack>
                        </Box>
                    </SkeletonWrapper>
                </SkeletonTheme>
            </Grommet>
        </MsalProvider>
    );
}

export default App;
